import { marketplaceModes, marketplaces } from "./labels";
import { marketplaceDescription, marketplaceTitle } from "./logic";
import { money } from "./money";
import { syncNotifications } from "./notifications";
import { defaultSubscriptionForWorkspace, entitlementsForSubscription } from "./entitlements";
import type {
  InventoryCategory,
  InventoryItem,
  ItemPhoto,
  ItemResearch,
  Marketplace,
  MarketplaceAccount,
  MarketplaceListing,
  ReOSDatabase,
  ReOSNotification,
  Sale,
  StoredAsset,
  User,
  Workspace
} from "./types";

export function makeID(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

export function emptyDatabase(): ReOSDatabase {
  return {
    users: [],
    workspaces: [],
    workspaceMembers: [],
    subscriptions: [],
    entitlements: [],
    inventoryItems: [],
    itemPhotos: [],
    storedAssets: [],
    marketplaceAccounts: [],
    marketplaceListings: [],
    marketplaceImportRuns: [],
    marketplaceImportRecords: [],
    sales: [],
    notifications: [],
    categories: []
  };
}

export function demoDatabase(): ReOSDatabase {
  const now = new Date();
  const user: User = {
    id: "user_demo",
    email: "demo@reos.local",
    name: "Alex Morgan",
    currency: "GBP",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    createdAt: daysAgo(now, 90),
    updatedAt: now.toISOString()
  };
  const workspace: Workspace = {
    id: "workspace_demo",
    ownerUserID: user.id,
    name: "North Loop Vintage",
    defaultCurrency: "GBP",
    marketplaceSignalEmail: "events+demo@in.listwork.co",
    marketplaceSignalsEnabledAt: daysAgo(now, 28),
    marketplaceSignalsLastReceivedAt: daysAgo(now, 1),
    createdAt: daysAgo(now, 90),
    updatedAt: now.toISOString()
  };

  const categories = makeDefaultCategories(workspace.id, now);
  const subscription = defaultSubscriptionForWorkspace(workspace.id, now.toISOString(), "starter");
  const entitlements = entitlementsForSubscription(subscription);
  const workspaceMember = {
    id: `member_${workspace.id}_${user.id}`,
    workspaceID: workspace.id,
    userID: user.id,
    role: "owner" as const,
    joinedAt: workspace.createdAt,
    createdAt: workspace.createdAt,
    updatedAt: workspace.updatedAt
  };
  const accounts = marketplaces.map<MarketplaceAccount>((marketplace) => ({
    id: `account_${marketplace}`,
    workspaceID: workspace.id,
    marketplace,
    accountName: `${displayMarketplace(marketplace)} Manual`,
    connectionStatus: "manual",
    integrationMode: marketplaceModes[marketplace],
    createdAt: daysAgo(now, 80),
    updatedAt: daysAgo(now, 2)
  }));

  const items: InventoryItem[] = [
    {
      id: "item_levis_501",
      workspaceID: workspace.id,
      title: "Vintage Levi's Type III denim jacket",
      description: "Classic blue denim trucker jacket with copper-tone buttons and an even vintage fade.",
      categoryID: "Clothing / Denim",
      condition: "good",
      brand: "Levi's",
      size: "M",
      color: "Blue",
      material: "Denim",
      sku: "LEVI-JACKET-M-0001",
      stockLocation: "Rack A / Box 4",
      purchaseCost: money(2600),
      targetPrice: money(8900),
      quantity: 1,
      status: "listed",
      acquiredAt: daysAgo(now, 18),
      listedAt: daysAgo(now, 10),
      createdAt: daysAgo(now, 18),
      updatedAt: daysAgo(now, 1)
    },
    {
      id: "item_ceramic_lamp",
      workspaceID: workspace.id,
      title: "Habitat ceramic table lamp",
      description: "Textured cream ceramic lamp with a clean linen shade and original brass-tone fittings.",
      categoryID: "Home / Lighting",
      condition: "like_new",
      brand: "Habitat",
      color: "Cream",
      material: "Ceramic",
      sku: "HAB-LAMP-CER-0002",
      stockLocation: "Studio shelf A2",
      purchaseCost: money(1800),
      targetPrice: money(6400),
      quantity: 1,
      status: "ready_to_list",
      acquiredAt: daysAgo(now, 12),
      createdAt: daysAgo(now, 12),
      updatedAt: daysAgo(now, 1)
    },
    {
      id: "item_nike_cortez",
      workspaceID: workspace.id,
      title: "Vintage white leather trainers",
      description: "Low-profile white leather trainers with black side detailing and light, honest wear.",
      categoryID: "Shoes / Trainers",
      condition: "good",
      size: "UK 8",
      color: "White",
      material: "Leather",
      sku: "TRAINER-WHT-UK8-0003",
      stockLocation: "Shoe wall",
      purchaseCost: money(3200),
      targetPrice: money(7200),
      quantity: 1,
      status: "listed",
      acquiredAt: daysAgo(now, 76),
      listedAt: daysAgo(now, 61),
      createdAt: daysAgo(now, 76),
      updatedAt: daysAgo(now, 3)
    },
    {
      id: "item_wool_coat",
      workspaceID: workspace.id,
      title: "Camel wool overcoat",
      description: "Long camel wool coat with clean lining and horn buttons.",
      categoryID: "Clothing / Outerwear",
      condition: "good",
      brand: "Jaeger",
      size: "M",
      color: "Camel",
      material: "Wool",
      sku: "JAEG-COAT-M-0004",
      stockLocation: "Rack B",
      purchaseCost: money(3800),
      targetPrice: money(12900),
      quantity: 0,
      status: "sold",
      acquiredAt: daysAgo(now, 50),
      listedAt: daysAgo(now, 44),
      soldAt: daysAgo(now, 1),
      createdAt: daysAgo(now, 50),
      updatedAt: daysAgo(now, 1)
    },
    {
      id: "item_record_player",
      workspaceID: workspace.id,
      title: "Bush portable record player",
      description: "Compact black portable record player, fully tested with a clean stylus and working speakers.",
      categoryID: "Electronics / Audio",
      condition: "fair",
      brand: "Bush",
      color: "Black",
      sku: "BUSH-REC-0005",
      stockLocation: "Electronics shelf",
      purchaseCost: money(1500),
      targetPrice: money(5800),
      quantity: 1,
      status: "listed",
      acquiredAt: daysAgo(now, 98),
      listedAt: daysAgo(now, 83),
      createdAt: daysAgo(now, 98),
      updatedAt: daysAgo(now, 7)
    },
    {
      id: "item_studio_vase",
      workspaceID: workspace.id,
      title: "Unsigned stoneware bottle vase",
      description: "Dark-glazed studio pottery bottle vase with a tactile iron-rich surface and an unsigned base.",
      categoryID: "Home / Ceramics",
      condition: "good",
      color: "Black",
      material: "Stoneware",
      sku: "STUDIO-VASE-0006",
      stockLocation: "Research shelf",
      purchaseCost: money(1200),
      targetPrice: money(4800),
      minimumPrice: money(3800),
      quantity: 1,
      status: "draft",
      acquiredAt: daysAgo(now, 5),
      research: studioVaseResearch(now),
      createdAt: daysAgo(now, 5),
      updatedAt: daysAgo(now, 1)
    },
    {
      id: "item_leather_satchel",
      workspaceID: workspace.id,
      title: "Vintage oxblood leather satchel",
      description: "Structured oxblood leather satchel with brass hardware, adjustable strap, and a warm vintage patina.",
      categoryID: "Accessories / Bags",
      condition: "good",
      color: "Oxblood",
      material: "Leather",
      sku: "SATCHEL-OXB-0007",
      stockLocation: "Accessories cabinet",
      purchaseCost: money(2800),
      targetPrice: money(7800),
      quantity: 1,
      status: "listed",
      acquiredAt: daysAgo(now, 26),
      listedAt: daysAgo(now, 16),
      createdAt: daysAgo(now, 26),
      updatedAt: daysAgo(now, 2)
    },
    {
      id: "item_brass_candlesticks",
      workspaceID: workspace.id,
      title: "Pair of vintage brass candlesticks",
      description: "Matched brass candlesticks with balanced proportions and a softly polished vintage patina.",
      categoryID: "Home / Decor",
      condition: "good",
      color: "Brass",
      material: "Brass",
      sku: "BRASS-CANDLE-0008",
      stockLocation: "Sold archive",
      purchaseCost: money(1600),
      targetPrice: money(7200),
      quantity: 0,
      status: "sold",
      acquiredAt: daysAgo(now, 32),
      listedAt: daysAgo(now, 21),
      soldAt: daysAgo(now, 4),
      createdAt: daysAgo(now, 32),
      updatedAt: daysAgo(now, 3)
    },
    {
      id: "item_teak_magazine_rack",
      workspaceID: workspace.id,
      title: "1960s teak magazine rack",
      description: "Compact mid-century teak magazine rack with slatted sides, tapered legs, and an integrated handle.",
      categoryID: "Home / Furniture",
      condition: "good",
      color: "Teak",
      material: "Teak",
      sku: "TEAK-RACK-0009",
      stockLocation: "Dispatch bay",
      purchaseCost: money(3200),
      targetPrice: money(11900),
      quantity: 0,
      status: "sold",
      acquiredAt: daysAgo(now, 58),
      listedAt: daysAgo(now, 38),
      soldAt: daysAgo(now, 12),
      createdAt: daysAgo(now, 58),
      updatedAt: daysAgo(now, 2)
    },
    {
      id: "item_welsh_blanket",
      workspaceID: workspace.id,
      title: "Vintage Welsh wool tapestry blanket",
      description: "Warm woven wool blanket in cream, rust, olive, and charcoal with intact fringe and a bold geometric repeat.",
      categoryID: "Home / Textiles",
      condition: "good",
      color: "Cream / Rust",
      material: "Wool",
      sku: "WELSH-BLANKET-0010",
      stockLocation: "Sold archive",
      purchaseCost: money(2300),
      targetPrice: money(8400),
      quantity: 0,
      status: "archived",
      acquiredAt: daysAgo(now, 66),
      listedAt: daysAgo(now, 51),
      soldAt: daysAgo(now, 25),
      createdAt: daysAgo(now, 66),
      updatedAt: daysAgo(now, 18)
    },
    {
      id: "item_studio_bottle",
      workspaceID: workspace.id,
      title: "Studio pottery ash-glaze bottle vase",
      description: "Hand-thrown stoneware bottle vase with a mottled ash glaze and a narrow pulled neck.",
      categoryID: "Home / Ceramics",
      condition: "good",
      color: "Brown",
      material: "Stoneware",
      sku: "STUDIO-BOTTLE-0011",
      stockLocation: "Returns shelf",
      purchaseCost: money(1400),
      targetPrice: money(5600),
      quantity: 0,
      status: "sold",
      acquiredAt: daysAgo(now, 70),
      listedAt: daysAgo(now, 55),
      soldAt: daysAgo(now, 40),
      createdAt: daysAgo(now, 70),
      updatedAt: daysAgo(now, 34)
    }
  ];

  const storedAssets: StoredAsset[] = items.map((item) => {
    const productPhoto = demoProductPhotoFor(item.id);
    return {
      id: `asset_${item.id}_original`,
      workspaceID: workspace.id,
      ownerUserID: user.id,
      itemID: item.id,
      kind: "original_photo",
      provider: "local_demo",
      storageKey: `demo/${workspace.id}/${item.id}/${productPhoto.fileName}`,
      publicURL: productPhoto.url,
      mimeType: productPhoto.mimeType,
      byteSize: productPhoto.byteSize,
      width: productPhoto.width,
      height: productPhoto.height,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt
    };
  });

  const itemPhotos: ItemPhoto[] = items.map((item) => {
    const asset = storedAssets.find((candidate) => candidate.itemID === item.id);
    if (!asset?.publicURL || !asset.width || !asset.height) {
      throw new Error(`Demo item ${item.id} is missing its product photograph.`);
    }
    return {
      id: `photo_${item.id}`,
      itemID: item.id,
      assetID: asset.id,
      url: asset.publicURL ?? "",
      thumbnailURL: asset.publicURL ?? "",
      sortOrder: 0,
      type: "original",
      width: asset.width,
      height: asset.height,
      treatment: {
        crop: "original",
        brightness: 100,
        contrast: 100,
        rotation: 0
      },
      createdAt: item.createdAt
    };
  });

  const itemByID = new Map(items.map((item) => [item.id, item]));
  const demoItem = (id: string) => {
    const item = itemByID.get(id);
    if (!item) {
      throw new Error(`Demo inventory item ${id} is missing.`);
    }
    return item;
  };

  const listings: MarketplaceListing[] = [
    listingFor(demoItem("item_levis_501"), accounts[2], "live", 342, 27, daysAgo(now, 1), now),
    listingFor(demoItem("item_levis_501"), accounts[0], "ready", 0, 0, daysAgo(now, 2), now),
    listingFor(demoItem("item_ceramic_lamp"), accounts[1], "ready", 0, 0, daysAgo(now, 1), now),
    listingFor(demoItem("item_nike_cortez"), accounts[0], "live", 214, 19, daysAgo(now, 3), now),
    listingFor(demoItem("item_nike_cortez"), accounts[3], "failed", 0, 0, daysAgo(now, 4), now, ["Add the UK size to the first line of the description."]),
    listingFor(demoItem("item_wool_coat"), accounts[1], "sold", 481, 31, daysAgo(now, 1), now),
    listingFor(demoItem("item_record_player"), accounts[2], "live", 88, 7, daysAgo(now, 7), now),
    listingFor(demoItem("item_record_player"), accounts[4], "live", 119, 5, daysAgo(now, 2), now),
    listingFor(demoItem("item_leather_satchel"), accounts[0], "live", 156, 22, daysAgo(now, 2), now),
    listingFor(demoItem("item_leather_satchel"), accounts[1], "draft", 0, 0, daysAgo(now, 1), now, ["Add parcel dimensions before posting."]),
    listingFor(demoItem("item_brass_candlesticks"), accounts[2], "sold", 205, 16, daysAgo(now, 4), now),
    listingFor(demoItem("item_teak_magazine_rack"), accounts[1], "sold", 390, 44, daysAgo(now, 12), now),
    listingFor(demoItem("item_welsh_blanket"), accounts[1], "sold", 267, 35, daysAgo(now, 25), now),
    listingFor(demoItem("item_studio_bottle"), accounts[2], "sold", 131, 9, daysAgo(now, 40), now)
  ];

  const sales: Sale[] = [
    {
      id: "sale_wool_coat",
      workspaceID: workspace.id,
      itemID: "item_wool_coat",
      listingID: "listing_item_wool_coat_etsy",
      marketplace: "etsy",
      status: "paid",
      salePrice: money(12900),
      shippingCharged: money(499),
      marketplaceFees: money(1185),
      paymentProcessingFees: money(329),
      shippingCost: money(420),
      packagingCost: money(95),
      purchaseCost: demoItem("item_wool_coat").purchaseCost,
      netProfit: money(7570),
      soldAt: daysAgo(now, 1),
      buyerName: "M. Price",
      buyerAddress: "18 Canonbury Lane",
      buyerPostcode: "N1 2PL",
      shipBy: daysAgo(now, -1),
      createdAt: daysAgo(now, 1),
      updatedAt: daysAgo(now, 1)
    },
    {
      id: "sale_brass_candlesticks",
      workspaceID: workspace.id,
      itemID: "item_brass_candlesticks",
      listingID: "listing_item_brass_candlesticks_ebay",
      marketplace: "ebay",
      status: "packed",
      salePrice: money(7200),
      shippingCharged: money(399),
      marketplaceFees: money(720),
      paymentProcessingFees: money(185),
      shippingCost: money(380),
      packagingCost: money(70),
      purchaseCost: demoItem("item_brass_candlesticks").purchaseCost,
      netProfit: money(4644),
      soldAt: daysAgo(now, 4),
      buyerName: "Helen Carter",
      buyerAddress: "42 Albert Road",
      buyerPostcode: "BS6 5QJ",
      shipBy: daysAgo(now, 0),
      fulfilmentNotes: "Wrapped separately and double boxed.",
      packedAt: daysAgo(now, 3),
      createdAt: daysAgo(now, 4),
      updatedAt: daysAgo(now, 3)
    },
    {
      id: "sale_teak_magazine_rack",
      workspaceID: workspace.id,
      itemID: "item_teak_magazine_rack",
      listingID: "listing_item_teak_magazine_rack_etsy",
      marketplace: "etsy",
      status: "shipped",
      salePrice: money(11900),
      shippingCharged: money(899),
      marketplaceFees: money(1428),
      paymentProcessingFees: money(320),
      shippingCost: money(650),
      packagingCost: money(150),
      purchaseCost: demoItem("item_teak_magazine_rack").purchaseCost,
      netProfit: money(7051),
      soldAt: daysAgo(now, 12),
      buyerName: "Daniel Morris",
      buyerAddress: "7 St James Terrace",
      buyerPostcode: "LS6 2AP",
      shipBy: daysAgo(now, 9),
      shippingCarrier: "Royal Mail",
      shippingService: "Tracked 48",
      trackingNumber: "LW-DEMO-48392017",
      fulfilmentNotes: "Handle protected with foam; legs corner wrapped.",
      packedAt: daysAgo(now, 11),
      shippedAt: daysAgo(now, 10),
      createdAt: daysAgo(now, 12),
      updatedAt: daysAgo(now, 10)
    },
    {
      id: "sale_welsh_blanket",
      workspaceID: workspace.id,
      itemID: "item_welsh_blanket",
      listingID: "listing_item_welsh_blanket_etsy",
      marketplace: "etsy",
      status: "completed",
      salePrice: money(8400),
      shippingCharged: money(499),
      marketplaceFees: money(924),
      paymentProcessingFees: money(225),
      shippingCost: money(470),
      packagingCost: money(80),
      purchaseCost: demoItem("item_welsh_blanket").purchaseCost,
      netProfit: money(4900),
      soldAt: daysAgo(now, 25),
      buyerName: "Sophie Ahmed",
      buyerPostcode: "M20 3DP",
      shippingCarrier: "Royal Mail",
      shippingService: "Tracked 48",
      trackingNumber: "LW-DEMO-37910422",
      packedAt: daysAgo(now, 24),
      shippedAt: daysAgo(now, 23),
      completedAt: daysAgo(now, 20),
      createdAt: daysAgo(now, 25),
      updatedAt: daysAgo(now, 20)
    },
    {
      id: "sale_studio_bottle",
      workspaceID: workspace.id,
      itemID: "item_studio_bottle",
      listingID: "listing_item_studio_bottle_ebay",
      marketplace: "ebay",
      status: "refunded",
      salePrice: money(5600),
      shippingCharged: money(399),
      marketplaceFees: money(0),
      paymentProcessingFees: money(0),
      shippingCost: money(0),
      packagingCost: money(0),
      purchaseCost: demoItem("item_studio_bottle").purchaseCost,
      netProfit: money(0),
      soldAt: daysAgo(now, 40),
      buyerName: "A. Wilson",
      buyerPostcode: "EH3 8BL",
      fulfilmentNotes: "Buyer cancelled before dispatch; no stock loss.",
      refundedAt: daysAgo(now, 39),
      createdAt: daysAgo(now, 40),
      updatedAt: daysAgo(now, 39)
    }
  ];

  const detectedSaleNotification: ReOSNotification = {
    id: "event_demo_vinted_sale",
    workspaceID: workspace.id,
    userID: user.id,
    source: "event",
    type: "marketplace_sale_detected",
    title: "Confirm Vinted sale for vintage leather satchel",
    body: "A forwarded Vinted email appears to report a £78.00 sale. Confirm it before inventory is updated.",
    itemID: "item_leather_satchel",
    listingID: "listing_item_leather_satchel_vinted",
    marketplaceSignal: {
      eventType: "sale_detected",
      marketplace: "vinted",
      confidence: "high",
      fingerprint: "demo-vinted-sale-satchel",
      senderDomain: "vinted.co.uk",
      subject: "Sold! Vintage oxblood leather satchel",
      occurredAt: daysAgo(now, 1),
      amountMinor: 7800,
      currency: "GBP",
      externalListingID: "demo-satchel-7831",
      externalOrderID: "demo-order-2048",
      parserVersion: "demo-v1"
    },
    createdAt: daysAgo(now, 1)
  };

  return syncNotifications({
    currentUserID: user.id,
    activeWorkspaceID: workspace.id,
    users: [user],
    workspaces: [workspace],
    workspaceMembers: [workspaceMember],
    subscriptions: [subscription],
    entitlements,
    inventoryItems: items,
    itemPhotos,
    storedAssets,
    marketplaceAccounts: accounts,
    marketplaceListings: listings,
    sales,
    notifications: [detectedSaleNotification],
    categories
  }, now);
}

export function makeDefaultCategories(workspaceID: string, now = new Date()): InventoryCategory[] {
  return [
    "Clothing / Denim",
    "Clothing / Outerwear",
    "Shoes / Trainers",
    "Home / Lighting",
    "Home / Ceramics",
    "Home / Decor",
    "Home / Furniture",
    "Home / Textiles",
    "Electronics / Audio",
    "Accessories / Bags",
    "Collectibles / Books"
  ].map((path) => ({
    id: `category_${path.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`,
    workspaceID,
    name: path.split("/").pop()?.trim() ?? path,
    path,
    isSystem: true,
    createdAt: daysAgo(now, 90),
    updatedAt: daysAgo(now, 90)
  }));
}

function listingFor(
  item: InventoryItem,
  account: MarketplaceAccount,
  status: MarketplaceListing["status"],
  views: number,
  watchers: number,
  updatedAt: string,
  now: Date,
  validationWarnings: string[] = []
): MarketplaceListing {
  return {
    id: `listing_${item.id}_${account.marketplace}`,
    workspaceID: item.workspaceID,
    itemID: item.id,
    marketplaceAccountID: account.id,
    marketplace: account.marketplace,
    title: marketplaceTitle(item, account.marketplace),
    description: marketplaceDescription(item, account.marketplace),
    price: item.targetPrice,
    shippingPrice: account.marketplace === "etsy" || account.marketplace === "ebay" ? money(499) : undefined,
    categoryExternalID: item.categoryID.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    conditionExternalValue: item.condition,
    status,
    views,
    watchers,
    likes: Math.round(watchers * 1.6),
    listedAt: item.listedAt,
    endedAt: status === "sold" || status === "ended" ? daysAgo(now, 1) : undefined,
    validationWarnings,
    createdAt: item.createdAt,
    updatedAt
  };
}

function daysAgo(now: Date, days: number): string {
  const date = new Date(now);
  date.setDate(now.getDate() - days);
  return date.toISOString();
}

function displayMarketplace(marketplace: Marketplace): string {
  if (marketplace === "ebay") {
    return "eBay";
  }
  return marketplace.charAt(0).toUpperCase() + marketplace.slice(1);
}

const demoProductPhotos: Record<string, {
  fileName: string;
  url: string;
  mimeType: string;
  byteSize: number;
  width: number;
  height: number;
}> = {
  item_levis_501: demoProductPhoto("denim-jacket.jpg", 118_000, 640),
  item_ceramic_lamp: demoProductPhoto("ceramic-lamp.jpg", 40_000, 640),
  item_nike_cortez: demoProductPhoto("leather-trainers.jpg", 133_000, 900),
  item_wool_coat: demoProductPhoto("camel-overcoat.jpg", 146_000, 900),
  item_record_player: demoProductPhoto("record-player.jpg", 55_000, 640),
  item_studio_vase: demoProductPhoto("ceramic-vase.jpg", 43_000, 640),
  item_leather_satchel: demoProductPhoto("leather-satchel.jpg", 191_000, 900),
  item_brass_candlesticks: demoProductPhoto("brass-candlesticks.jpg", 112_000, 900),
  item_teak_magazine_rack: demoProductPhoto("teak-magazine-rack.jpg", 138_000, 900),
  item_welsh_blanket: demoProductPhoto("welsh-blanket.jpg", 330_000, 900),
  item_studio_bottle: {
    fileName: "studio-pottery-reference.png",
    url: "/demo-products/studio-pottery-reference.png",
    mimeType: "image/png",
    byteSize: 1_800_000,
    width: 1254,
    height: 1254
  }
};

function demoProductPhoto(fileName: string, byteSize: number, dimension: number) {
  return {
    fileName,
    url: `/demo-products/${fileName}`,
    mimeType: "image/jpeg",
    byteSize,
    width: dimension,
    height: dimension
  };
}

function demoProductPhotoFor(itemID: string) {
  const photo = demoProductPhotos[itemID];
  if (!photo) {
    throw new Error(`Demo product photograph for ${itemID} is missing.`);
  }
  return photo;
}

function studioVaseResearch(now: Date): ItemResearch {
  const completedAt = daysAgo(now, 1);
  return {
    status: "ready_to_refine",
    identification: {
      maker: "Leach Pottery",
      model: "Standard Ware bottle vase",
      period: "1970s",
      origin: "St Ives, Cornwall",
      material: "Stoneware",
      confidence: "medium",
      rationale: "The bottle form and dark iron glaze are consistent with the reference, but no maker's mark is visible."
    },
    evidence: [
      {
        id: "evidence_demo_studio_archive",
        type: "reference",
        decision: "included",
        sourceName: "National Gallery of Victoria",
        title: "Bernard Leach bottle vase (1959), accession 2003.136",
        sourceURL: "https://www.ngv.vic.gov.au/explore/collection/work/73035/",
        observedAt: daysAgo(now, 2),
        notes: "The record documents a stoneware bottle vase made in St Ives. The comparison image is representative demo media.",
        sourceCategory: "institutional",
        relationship: "supports",
        confidence: "medium",
        excerpt: "The collection record supports bottle-vase form, stoneware material, and St Ives context; it does not prove this item's maker.",
        imageURL: "/demo-products/studio-pottery-reference.png",
        imageProvenance: "synthetic_demo",
        imageDisclosure: "Representative image for this demo; not source evidence.",
        retrievedAt: daysAgo(now, 1),
        independenceKey: "demo-studio-pottery-archive"
      },
      {
        id: "evidence_demo_marketplace",
        type: "active_listing",
        decision: "excluded",
        sourceName: "Marketplace result",
        title: "Unsigned dark-glazed studio pottery bottle",
        sourceURL: "https://www.ebay.co.uk/",
        price: money(6800),
        condition: "Good",
        observedAt: daysAgo(now, 2),
        notes: "Asking-price context only; the seller attribution is unverified.",
        sourceCategory: "marketplace",
        relationship: "context",
        confidence: "low",
        retrievedAt: daysAgo(now, 1),
        independenceKey: "demo-marketplace-result"
      }
    ],
    refinement: {
      positioning: "Unsigned British studio pottery with a possible Leach Pottery attribution",
      title: "Attributed Leach Pottery Stoneware Bottle Vase, 1970s",
      description: "An unsigned hand-thrown stoneware bottle vase with a dark iron-rich glaze. The form is consistent with Leach Pottery Standard Ware, but the attribution remains provisional without a visible mark.",
      targetPrice: money(6200),
      minimumPrice: money(4600),
      pricingRationale: "Draft target only. Confirm against condition-adjusted sold records before applying a price independently of the unresolved attribution."
    },
    run: {
      id: "run_demo_studio_vase",
      depth: "standard",
      focus: "verify",
      templateVersion: "verify-attribution-v1",
      customPriority: "Test whether the available evidence supports the proposed Leach Pottery attribution.",
      photoIDs: ["photo_item_studio_vase"],
      status: "ready",
      phase: "Ready for review",
      progress: 100,
      brief: "Verify the maker, period, and material as precisely as the available evidence allows.",
      sellerContext: "Found in Cornwall with no visible signature or paper label.",
      plan: [
        { id: "plan_demo_inspect", label: "Inspect", detail: "Read form, glaze, base, and visible construction.", status: "complete" },
        { id: "plan_demo_visual", label: "Find visual matches", detail: "Compare full-object and detail candidates.", status: "complete" },
        { id: "plan_demo_sources", label: "Research sources", detail: "Check specialist and marketplace references.", status: "complete" },
        { id: "plan_demo_compare", label: "Compare hypotheses", detail: "Test attribution against alternatives.", status: "complete" },
        { id: "plan_demo_verify", label: "Verify claims", detail: "Separate observed facts from inference.", status: "complete" }
      ],
      observations: [
        {
          id: "observation_demo_form",
          label: "Form",
          value: "Wheel-thrown bottle form with narrow neck and rounded shoulder.",
          basis: "photo",
          confidence: "high",
          photoID: "photo_item_studio_vase"
        },
        {
          id: "observation_demo_mark",
          label: "Maker's mark",
          value: "No mark is visible in the supplied photograph.",
          basis: "photo",
          confidence: "high",
          photoID: "photo_item_studio_vase"
        }
      ],
      hypotheses: [
        {
          id: "hypothesis_demo_leach",
          label: "Leach Pottery Standard Ware",
          confidence: "medium",
          summary: "Form and glaze align, but the missing base mark prevents a defensible maker-level identification.",
          supportingClaimIDs: ["claim_demo_identity"],
          conflictingClaimIDs: []
        }
      ],
      claims: [
        {
          id: "claim_demo_identity",
          kind: "identity",
          statement: "This may be a Leach Pottery Standard Ware bottle vase from the 1970s.",
          verdict: "supported",
          confidence: "medium",
          rationale: "The selected studio pottery archive reference supports the form and glaze, but the photographs do not show a maker's mark.",
          evidenceIDs: ["evidence_demo_studio_archive"],
          sellerDecision: "pending"
        }
      ],
      questions: [
        {
          id: "question_demo_base",
          prompt: "Add a square-on photograph of the base and any impressed mark.",
          reason: "A visible mark would distinguish a Leach Pottery attribution from a similar unsigned studio piece.",
          requestedInput: "photo",
          priority: "high"
        }
      ],
      searches: [
        { id: "search_demo_visual", query: "dark glazed studio pottery bottle vase visual match", method: "visual", resultCount: 12 },
        { id: "search_demo_archive", query: "Leach Pottery Standard Ware bottle vase 1970s", method: "web", resultCount: 8 }
      ],
      verification: {
        checkedClaimCount: 1,
        supportedClaimCount: 1,
        contestedClaimCount: 0,
        unsupportedClaimCount: 0,
        summary: "The proposed attribution is plausible but should remain provisional until the base is documented.",
        completedAt
      },
      startedAt: daysAgo(now, 2),
      updatedAt: completedAt,
      completedAt
    },
    provider: {
      id: "listwork_demo_research",
      label: "Listwork visual evidence research",
      mode: "connected",
      generatedAt: completedAt
    },
    updatedAt: completedAt
  };
}
